/**
 * themeStore — dark / yok-light toggle.
 *
 * Mirrors main jubit-ai-universe convention: <html class="dark|light">.
 * Tokens in src/tokens/jubit-tokens.css resolve differently per class.
 *
 * Default is dark (matches the "Jubuddy dark mode" house style). Light mode
 * is yok-jubit — warm parchment + ink text.
 *
 * Persists via zustand/middleware so the user's choice survives reloads.
 * The effect in applyThemeToHtml ensures the class is synced to <html> on
 * hydration and on every change.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type ThemeId = 'dark' | 'light';

interface ThemeState {
  theme: ThemeId;
  setTheme: (t: ThemeId) => void;
  toggle: () => void;
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      theme: 'dark',
      setTheme: (t) => set({ theme: t }),
      toggle: () => set({ theme: get().theme === 'dark' ? 'light' : 'dark' }),
    }),
    { name: 'jubuddy:theme-store:v1' },
  ),
);

/**
 * Apply the current theme class to <html>. Idempotent — safe to call from
 * effects or imperatively.
 */
export function applyThemeToHtml(theme: ThemeId): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.classList.remove('dark', 'light');
  root.classList.add(theme);
}
